import i18n from "@/i18n";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import {
    Search, X, ArrowRight, LayoutGrid, Boxes, ChefHat, ClipboardCheck,
    Truck, Building2, Bell, Building, Users as UsersIcon, BarChart3,
    Cog, CreditCard, Package, LifeBuoy, Sparkles, Bug, GraduationCap,
} from "lucide-react";
import { toast } from "sonner";

/* ---------- Configuration: which buckets the palette can show ---------- */

// Restaurant / Corporate buckets (kitchen data). Backend: /api/search
const KITCHEN_BUCKETS = [
    // ordered by Prompt 15 result priority
    { key: "tasks", get label() { return i18n.t("ui.CommandPalette.KITCHEN_BUCKETS.label_workflow", "Workflow"); }, icon: LayoutGrid,
      titleField: "title", subtitleField: "description",
      onOpen: (item, nav) => nav("/workflow") },
    { key: "products", get label() { return i18n.t("ui.CommandPalette.KITCHEN_BUCKETS.label_products", "Products"); }, icon: Boxes,
      titleField: "product_name",
      subtitle: (it) => `${it.unit || ""}${it.last_price ? " · € " + Number(it.last_price).toFixed(2) : ""}`,
      onOpen: (_it, nav) => nav("/inventory") },
    { key: "recipes", get label() { return i18n.t("ui.CommandPalette.KITCHEN_BUCKETS.label_recipes", "Recipes"); }, icon: ChefHat,
      titleField: "name",
      subtitle: (it) => it.category || "",
      onOpen: (_it, nav) => nav("/recipes") },
    { key: "inventory", get label() { return i18n.t("ui.CommandPalette.KITCHEN_BUCKETS.label_inventoryCounts", "Inventory counts"); }, icon: ClipboardCheck,
      title: (it) => `${it.count_type || i18n.t("ui.commandPalette.count", "Count")} — ${it.count_date || ""}`,
      subtitle: (it) => `${it.area_name || ""} · ${it.status}`,
      onOpen: (_it, nav) => nav("/inventory") },
    { key: "deliveries", get label() { return i18n.t("ui.CommandPalette.KITCHEN_BUCKETS.label_deliveries", "Deliveries"); }, icon: Truck,
      title: (it) => `${it.supplier_name || i18n.t("ui.commandPalette.delivery", "Delivery")} — ${it.delivery_date || ""}`,
      subtitle: (it) => `${it.status}${it.notes ? " · " + it.notes : ""}`,
      onOpen: (_it, nav) => nav("/deliveries") },
    { key: "suppliers", get label() { return i18n.t("ui.CommandPalette.KITCHEN_BUCKETS.label_suppliers", "Suppliers"); }, icon: Building2,
      titleField: "name",
      subtitle: (it) => [it.category, it.phone].filter(Boolean).join(" · "),
      onOpen: (_it, nav) => nav("/suppliers") },
    { key: "daily_checks", get label() { return i18n.t("ui.CommandPalette.KITCHEN_BUCKETS.label_dailyChecks", "Daily checks"); }, icon: ClipboardCheck,
      title: (it) => `Daily checks — ${it.check_date || ""}`,
      subtitle: (it) => it.status,
      onOpen: (_it, nav) => nav("/daily-checks") },
    { key: "announcements", get label() { return i18n.t("ui.CommandPalette.KITCHEN_BUCKETS.label_announcements", "Announcements"); }, icon: Bell,
      titleField: "title",
      subtitle: (it) => it.message?.slice(0, 90) || "",
      onOpen: (_it, nav) => nav("/announcements") },
    { key: "restaurants", get label() { return i18n.t("ui.CommandPalette.KITCHEN_BUCKETS.label_restaurants", "Restaurants"); }, icon: Building,
      titleField: "name",
      subtitle: (it) => it.city || "",
      onOpen: (_it, nav) => nav("/settings") },
    { key: "users", get label() { return i18n.t("ui.CommandPalette.KITCHEN_BUCKETS.label_users", "Users"); }, icon: UsersIcon,
      titleField: "name",
      subtitle: (it) => it.email || "",
      onOpen: (_it, nav) => nav("/settings") },
];

// Super Admin buckets (platform/SaaS data). Backend: /api/admin/search
const PLATFORM_BUCKETS = [
    { key: "companies", get label() { return i18n.t("ui.CommandPalette.PLATFORM_BUCKETS.label_companies", "Companies"); }, icon: Building2,
      titleField: "name",
      subtitle: (it) => [it.lifecycle_status, it.country].filter(Boolean).join(" · "),
      onOpen: (_it, nav) => nav("/admin?tab=companies") },
    { key: "restaurants", get label() { return i18n.t("ui.CommandPalette.PLATFORM_BUCKETS.label_restaurants", "Restaurants"); }, icon: Building,
      titleField: "name",
      subtitle: (it) => it.city || "",
      onOpen: (_it, nav) => nav("/admin?tab=restaurants") },
    { key: "users", get label() { return i18n.t("ui.CommandPalette.PLATFORM_BUCKETS.label_users", "Users"); }, icon: UsersIcon,
      titleField: "name",
      subtitle: (it) => `${it.email || ""}${it.role ? " · " + it.role : ""}`,
      onOpen: (_it, nav) => nav("/admin?tab=users") },
    { key: "plans", get label() { return i18n.t("ui.CommandPalette.PLATFORM_BUCKETS.label_plans", "Plans"); }, icon: Package,
      titleField: "name",
      subtitle: (it) => it.code || "",
      onOpen: (_it, nav) => nav("/admin?tab=plans") },
    { key: "subscriptions", get label() { return i18n.t("ui.CommandPalette.PLATFORM_BUCKETS.label_subscriptions", "Subscriptions"); }, icon: CreditCard,
      title: (it) => it.billing_email || it.company_id || "Subscription",
      subtitle: (it) => `${it.plan || ""} · ${it.status || ""}`,
      onOpen: (_it, nav) => nav("/admin?tab=subscriptions") },
    { key: "support_notes", get label() { return i18n.t("ui.CommandPalette.PLATFORM_BUCKETS.label_supportNotes", "Support Notes"); }, icon: LifeBuoy,
      title: (it) => (it.text || "").slice(0, 80) || "Support note",
      subtitle: (it) => `${it.priority || ""} · ${it.status || ""}`,
      onOpen: (_it, nav) => nav("/admin?tab=support") },
    { key: "feature_requests", get label() { return i18n.t("ui.CommandPalette.PLATFORM_BUCKETS.label_featureRequests", "Feature Requests"); }, icon: Sparkles,
      titleField: "title",
      subtitle: (it) => `${it.module || ""} · ${it.status || ""}`,
      onOpen: (_it, nav) => nav("/admin?tab=features") },
    { key: "bug_reports", get label() { return i18n.t("ui.CommandPalette.PLATFORM_BUCKETS.label_bugReports", "Bug Reports"); }, icon: Bug,
      titleField: "title",
      subtitle: (it) => `${it.severity || ""} · ${it.status || ""}`,
      onOpen: (_it, nav) => nav("/admin?tab=bugs") },
    { key: "training", get label() { return i18n.t("ui.CommandPalette.PLATFORM_BUCKETS.label_training", "Training"); }, icon: GraduationCap,
      title: (it) => `${it.course || "Course"} · ${it.role || ""}`,
      subtitle: (it) => it.status || "",
      onOpen: (_it, nav) => nav("/admin?tab=training") },
];

/* ---------- Static report deeplinks (Prompt 15 — Reports Integration) -- */

const REPORT_KEYWORDS = [
    { match: /\binventory\s*variance|stock\s*variance|variance/i,
      get label() { return i18n.t("ui.CommandPalette.REPORT_KEYWORDS.label_inventoryVarianceReport", "Inventory variance report"); }, route: "/reports?kind=inventory" },
    { match: /\bfood\s*waste|waste\s*report|waste/i,
      get label() { return i18n.t("ui.CommandPalette.REPORT_KEYWORDS.label_foodWasteReport", "Food waste report"); }, route: "/reports?kind=workflow" },
    { match: /\bsupplier\s*purchases|purchases\s*by\s*supplier|purchases/i,
      get label() { return i18n.t("ui.CommandPalette.REPORT_KEYWORDS.label_supplierPurchasesReport", "Supplier purchases report"); }, route: "/reports?kind=purchasing" },
    { match: /\bdaily\s*checks?|compliance|omavalvonta/i,
      get label() { return i18n.t("ui.CommandPalette.REPORT_KEYWORDS.label_dailyChecksComplianceReport", "Daily checks compliance report"); }, route: "/reports?kind=daily-checks" },
    { match: /\bworkflow|tasks?/i,
      get label() { return i18n.t("ui.CommandPalette.REPORT_KEYWORDS.label_workflowReport", "Workflow report"); }, route: "/reports?kind=workflow" },
    { match: /\bsupplier/i,
      get label() { return i18n.t("ui.CommandPalette.REPORT_KEYWORDS.label_suppliersReport", "Suppliers report"); }, route: "/reports?kind=suppliers" },
];

/* ---------- Static application-module deeplinks (R1.4) ---------------
 * Global Search must be able to navigate to top-level MiseOne modules
 * (e.g. "Production Review") in addition to searching tenant data.
 * Kitchen-only: not surfaced to super_admin. Only routes that already
 * exist in App.js are listed here — no invented routes. Workforce and
 * Omavalvonta / Daily Checks are intentionally excluded per prompt. */
const MODULE_LINKS = [
    { get label() { return i18n.t("ui.CommandPalette.MODULE_LINKS.label_workflow", "Workflow"); },         route: "/workflow",         icon: LayoutGrid },
    { get label() { return i18n.t("ui.CommandPalette.MODULE_LINKS.label_weeklyMenu", "Weekly Menu"); },      route: "/weekly-menu",      icon: ChefHat },
    { get label() { return i18n.t("ui.CommandPalette.MODULE_LINKS.label_recipes", "Recipes"); },          route: "/recipes",          icon: ChefHat },
    { get label() { return i18n.t("ui.CommandPalette.MODULE_LINKS.label_inventory", "Inventory"); },        route: "/inventory",        icon: Boxes },
    { get label() { return i18n.t("ui.CommandPalette.MODULE_LINKS.label_deliveries", "Deliveries"); },       route: "/deliveries",       icon: Truck },
    { get label() { return i18n.t("ui.CommandPalette.MODULE_LINKS.label_suppliers", "Suppliers"); },        route: "/suppliers",        icon: Building2 },
    { get label() { return i18n.t("ui.CommandPalette.MODULE_LINKS.label_dailyPerformance", "Daily Performance"); }, route: "/production-review", icon: ClipboardCheck },
    { get label() { return i18n.t("ui.CommandPalette.MODULE_LINKS.label_reports", "Reports"); },          route: "/reports",          icon: BarChart3 },
];

/* ---------- LocalStorage helpers ---------- */

const RECENT_KEY = "ms.search.recent";
const loadRecent = () => {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); }
    catch { return []; }
};
const saveRecent = (q) => {
    if (!q?.trim()) return;
    const cur = loadRecent().filter((x) => x !== q);
    cur.unshift(q);
    localStorage.setItem(RECENT_KEY, JSON.stringify(cur.slice(0, 8)));
};

/* ---------- Main component ---------- */

export default function CommandPalette({ open, onClose }) {
    const navigate = useNavigate();
    const { user } = useApp();
    const isSuperAdmin = user?.role === "super_admin";
    const BUCKETS = isSuperAdmin ? PLATFORM_BUCKETS : KITCHEN_BUCKETS;
    const searchUrl = isSuperAdmin ? "/admin/search" : "/search";
    const placeholder = isSuperAdmin
        ? i18n.t("ui.commandPalette.searchCompaniesUsersSubscriptionsSupport", "Search companies, users, subscriptions, support…")
        : i18n.t("ui.commandPalette.searchRecipesProductsSuppliersTasks", "Search recipes, products, suppliers, tasks…");

    const [q, setQ] = useState("");
    const [results, setResults] = useState({});
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("all");
    const [activeIdx, setActiveIdx] = useState(0);
    const inputRef = useRef(null);

    // Reset on open / focus the input
    useEffect(() => {
        if (open) {
            setQ(""); setResults({}); setActiveIdx(0); setFilter("all");
            setTimeout(() => inputRef.current?.focus(), 30);
        }
    }, [open]);

    // Live, debounced search
    useEffect(() => {
        if (!open) return;
        if (!q.trim()) { setResults({}); return; }
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const { data } = await api.get(searchUrl, { params: { q } });
                setResults(data || {});
                setActiveIdx(0);
            } catch {
                setResults({});
            } finally { setLoading(false); }
        }, 150);
        return () => clearTimeout(t);
    }, [q, open, searchUrl]);

    // Build a flat list of selectable items respecting filter + priority
    const flatItems = useMemo(() => {
        const items = [];
        const buckets = filter === "all" ? BUCKETS
            : BUCKETS.filter((b) => b.key === filter);
        for (const b of buckets) {
            const rows = (results[b.key] || []);
            for (const r of rows) items.push({ bucket: b, item: r });
        }
        // Report deeplinks are kitchen-only.
        if (!isSuperAdmin && q.trim().length >= 2 && filter === "all") {
            for (const kw of REPORT_KEYWORDS) {
                if (kw.match.test(q)) {
                    items.push({
                        bucket: { key: "_report", label: i18n.t("ui.commandPalette.reports", "Reports"), icon: BarChart3,
                                    onOpen: (_it, nav) => nav(kw.route) },
                        item: { _label: kw.label, _route: kw.route },
                        isReport: true,
                    });
                }
            }
        }
        // R1.4 · Application-module navigation. Kitchen-only; case-
        // insensitive substring match against the module label. Uses
        // only routes that already exist in the app router.
        if (!isSuperAdmin && q.trim().length >= 1 && filter === "all") {
            const needle = q.trim().toLowerCase();
            for (const m of MODULE_LINKS) {
                if (m.label.toLowerCase().includes(needle)) {
                    const route = m.route;
                    items.push({
                        bucket: { key: "_module", label: i18n.t("ui.commandPalette.modules", "Modules"), icon: m.icon,
                                    onOpen: (_it, nav) => nav(route) },
                        item: { _label: m.label, _route: route },
                        isModule: true,
                    });
                }
            }
        }
        return items;
    }, [results, filter, q, BUCKETS, isSuperAdmin]);

    const openTarget = useCallback((target) => {
        saveRecent(q.trim());
        target.bucket.onOpen(target.item, navigate);
        onClose();
    }, [q, navigate, onClose]);

    // Keyboard navigation
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setActiveIdx((i) => Math.min(i + 1, flatItems.length - 1));
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActiveIdx((i) => Math.max(i - 1, 0));
            } else if (e.key === "Enter") {
                e.preventDefault();
                const target = flatItems[activeIdx];
                if (target) openTarget(target);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, flatItems, activeIdx, onClose, openTarget]);


    const recent = useMemo(loadRecent, [open]);

    // Empty-state quick actions
    const isEmpty = q.trim().length > 0 && !loading && flatItems.length === 0;

    if (!open) return null;

    return (
        <div
            data-testid="command-palette"
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4"
            onClick={onClose}
        >
            <div
                className="w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Search input */}
                <div className="flex items-center gap-3 px-5 py-4 border-b border-neutral-100">
                    <Search className="h-5 w-5 text-neutral-400 shrink-0" />
                    <input
                        ref={inputRef}
                        data-testid="palette-input"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder={placeholder}
                        className="flex-1 h-10 bg-transparent text-base focus:outline-none placeholder-neutral-400"
                    />
                    <kbd className="text-[10px] font-semibold text-neutral-400 border border-neutral-200 rounded px-1.5 py-0.5">{i18n.t("ui.commandPalette.esc", "ESC")}</kbd>
                    <button
                        data-testid="palette-close"
                        onClick={onClose}
                        className="text-neutral-400 hover:text-neutral-700 p-1 rounded"
                        aria-label={i18n.t("ui.commandPalette.closeSearch", "Close search")}
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {/* Module filter chips */}
                {q.trim().length > 0 && (
                    <div className="flex items-center gap-1 px-4 py-2 border-b border-neutral-100 overflow-x-auto" data-testid="palette-filters">
                        <FilterChip label={i18n.t("ui.commandPalette.all", "All")} value="all" active={filter} onClick={setFilter} />
                        {BUCKETS.map((b) => (
                            (results[b.key] || []).length > 0 && (
                                <FilterChip key={b.key} label={b.label} value={b.key} active={filter} onClick={setFilter}
                                    badge={(results[b.key] || []).length} />
                            )
                        ))}
                    </div>
                )}

                {/* Body */}
                <div className="max-h-[55vh] overflow-y-auto">
                    {q.trim().length === 0 && (
                        <SuggestionsView recent={recent} onPick={(t) => setQ(t)}
                            navigate={navigate} onClose={onClose}
                            isSuperAdmin={isSuperAdmin} />
                    )}

                    {q.trim().length > 0 && loading && (
                        <div className="px-5 py-4 text-sm text-neutral-500" data-testid="palette-loading">
                            {i18n.t("ui.commandPalette.searching", "Searching…")}
                        </div>
                    )}

                    {q.trim().length > 0 && !loading && flatItems.length > 0 && (
                        <ResultList items={flatItems} activeIdx={activeIdx}
                            onPick={openTarget} setActiveIdx={setActiveIdx} />
                    )}

                    {isEmpty && (
                        <EmptyState q={q} navigate={navigate} onClose={onClose}
                                     isSuperAdmin={isSuperAdmin} />
                    )}
                </div>

                {/* Footer hint */}
                <div className="flex items-center justify-between text-[11px] text-neutral-400 px-4 py-2 border-t border-neutral-100">
                    <span>
                        <kbd className="font-mono text-neutral-500">↑↓</kbd> {i18n.t("ui.commandPalette.hintNavigate", "navigate")}
                        &nbsp;·&nbsp;
                        <kbd className="font-mono text-neutral-500">↵</kbd> {i18n.t("ui.commandPalette.hintOpen", "open")}
                        &nbsp;·&nbsp;
                        <kbd className="font-mono text-neutral-500">{i18n.t("ui.commandPalette.esc", "ESC")}</kbd> {i18n.t("ui.commandPalette.hintClose", "close")}
                    </span>
                    <span className="font-medium">
                        {isSuperAdmin ? i18n.t("ui.commandPalette.miseonePlatformSearch", "MiseOne · Platform Search") : i18n.t("ui.commandPalette.miseoneGlobalSearch", "MiseOne · Global Search")}
                    </span>
                </div>
            </div>
        </div>
    );
}

function FilterChip({ label, value, active, onClick, badge }) {
    const isActive = active === value;
    return (
        <button
            data-testid={`palette-filter-${value}`}
            onClick={() => onClick(value)}
            className={`h-7 px-2.5 rounded-full text-[11px] font-semibold uppercase tracking-wide transition shrink-0 ${
                isActive
                    ? "bg-neutral-900 text-white"
                    : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
            }`}
        >
            {label}{badge !== undefined && (
                <span className={`ml-1.5 ${isActive ? "text-white/70" : "text-neutral-400"}`}>{badge}</span>
            )}
        </button>
    );
}

function ResultList({ items, activeIdx, onPick, setActiveIdx }) {
    // Group items by bucket for headers
    const grouped = [];
    let lastKey = null;
    items.forEach((it, idx) => {
        const key = it.bucket.key;
        if (key !== lastKey) {
            grouped.push({ header: it.bucket });
            lastKey = key;
        }
        grouped.push({ ...it, idx });
    });
    return (
        <ul data-testid="palette-results">
            {grouped.map((g, i) => {
                if (g.header) {
                    const Icon = g.header.icon;
                    return (
                        <li key={`h-${i}`} className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider font-semibold text-neutral-400 flex items-center gap-2">
                            <Icon className="h-3 w-3" /> {g.header.label}
                        </li>
                    );
                }
                const isActive = g.idx === activeIdx;
                const Icon = g.bucket.icon;
                const title = g.isReport
                    ? g.item._label
                    : g.isModule
                        ? g.item._label
                        : (typeof g.bucket.title === "function"
                            ? g.bucket.title(g.item)
                            : g.item[g.bucket.titleField]);
                const subtitle = g.isReport
                    ? i18n.t("ui.commandPalette.openReport", "Open report")
                    : g.isModule
                        ? i18n.t("ui.commandPalette.openModule", "Open module")
                        : (typeof g.bucket.subtitle === "function"
                            ? g.bucket.subtitle(g.item)
                            : g.item[g.bucket.subtitleField] || "");
                return (
                    <li key={`r-${i}-${g.item.id || g.idx}`}
                        data-testid={`palette-result-${g.bucket.key}-${g.item.id || g.idx}`}
                        onMouseEnter={() => setActiveIdx(g.idx)}
                        onClick={() => onPick(g)}
                        className={`px-4 py-2.5 mx-1 my-0.5 rounded-md cursor-pointer flex items-center gap-3 transition ${
                            isActive ? "bg-neutral-900 text-white" : "hover:bg-neutral-50"
                        }`}>
                        <Icon className={`h-4 w-4 shrink-0 ${isActive ? "text-white/80" : "text-neutral-400"}`} />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold truncate">{title || "—"}</div>
                            {subtitle && (
                                <div className={`text-xs truncate ${isActive ? "text-white/70" : "text-neutral-500"}`}>
                                    {subtitle}
                                </div>
                            )}
                        </div>
                        <ArrowRight className={`h-4 w-4 shrink-0 ${isActive ? "text-white" : "text-neutral-300"}`} />
                    </li>
                );
            })}
        </ul>
    );
}

function SuggestionsView({ recent, onPick, navigate, onClose, isSuperAdmin }) {
    const quickLinks = isSuperAdmin ? [
        { label: i18n.t("ui.commandPalette.dashboard", "Dashboard"),     route: "/admin?tab=dashboard",     icon: LayoutGrid },
        { label: i18n.t("ui.commandPalette.companies", "Companies"),     route: "/admin?tab=companies",     icon: Building2 },
        { label: i18n.t("ui.commandPalette.lifecycle", "Lifecycle"),     route: "/admin?tab=lifecycle",     icon: UsersIcon },
        { label: i18n.t("ui.commandPalette.support", "Support"),       route: "/admin?tab=support",       icon: LifeBuoy },
        { label: i18n.t("ui.commandPalette.commercial", "Commercial"),    route: "/admin?tab=commercial",    icon: Package },
        { label: i18n.t("ui.commandPalette.settings", "Settings"),      route: "/admin?tab=settings",      icon: Cog },
    ] : [
        { label: i18n.t("ui.commandPalette.workflow", "Workflow"),    route: "/workflow",    icon: LayoutGrid },
        { label: i18n.t("ui.commandPalette.inventory", "Inventory"),   route: "/inventory",   icon: Boxes },
        { label: i18n.t("ui.commandPalette.deliveries", "Deliveries"),  route: "/deliveries",  icon: Truck },
        { label: i18n.t("ui.commandPalette.recipes", "Recipes"),     route: "/recipes",     icon: ChefHat },
        { label: i18n.t("ui.commandPalette.suppliers", "Suppliers"),   route: "/suppliers",   icon: Building2 },
        { label: i18n.t("ui.commandPalette.settings", "Settings"),    route: "/settings",    icon: Cog },
    ];
    return (
        <div className="p-4 space-y-5" data-testid="palette-suggestions">
            {recent.length > 0 && (
                <div>
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-neutral-400 mb-2">{i18n.t("ui.commandPalette.recentSearches", "Recent searches")}</div>
                    <div className="flex flex-wrap gap-1.5">
                        {recent.map((r) => (
                            <button key={r} data-testid={`palette-recent-${r}`}
                                onClick={() => onPick(r)}
                                className="px-2.5 py-1 rounded-full bg-neutral-100 text-xs font-medium text-neutral-700 hover:bg-neutral-200">
                                {r}
                            </button>
                        ))}
                    </div>
                </div>
            )}
            <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-neutral-400 mb-2">{i18n.t("ui.commandPalette.jumpTo", "Jump to")}</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {quickLinks.map((q) => (
                        <button key={q.route} data-testid={`palette-quick-${q.label.toLowerCase()}`}
                            onClick={() => { navigate(q.route); onClose(); }}
                            className="h-10 px-3 inline-flex items-center gap-2 rounded-md border border-neutral-200 text-sm font-medium hover:bg-neutral-50">
                            <q.icon className="h-4 w-4 text-neutral-400" /> {q.label}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function EmptyState({ q, navigate, onClose, isSuperAdmin }) {
    const create = isSuperAdmin ? [
        { label: i18n.t("ui.commandPalette.newCompany", "New Company"),  route: "/admin?tab=companies&new=1" },
        { label: i18n.t("ui.commandPalette.newUser", "New User"),     route: "/admin?tab=users&new=1" },
        { label: i18n.t("ui.commandPalette.newPlan", "New Plan"),     route: "/admin?tab=plans&new=1" },
    ] : [
        { label: i18n.t("ui.commandPalette.createProduct", "Create product"),  route: "/inventory" },
        { label: i18n.t("ui.commandPalette.createSupplier", "Create supplier"), route: "/suppliers" },
        { label: i18n.t("ui.commandPalette.createRecipe", "Create recipe"),   route: "/recipes" },
    ];
    return (
        <div className="p-6 text-center" data-testid="palette-empty">
            <div className="text-sm font-semibold mb-1">No results for &ldquo;{q}&rdquo;</div>
            <div className="text-xs text-neutral-500 mb-4">{i18n.t("ui.commandPalette.wantToCreateSomethingInstead", "Want to create something instead?")}</div>
            <div className="flex justify-center gap-2 flex-wrap">
                {create.map((c) => (
                    <button key={c.label} data-testid={`palette-create-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                        onClick={() => { navigate(c.route); onClose(); toast.message(i18n.t("ui.commandPalette.useTheNewButtonOnThis", "Use the New button on this page to create the record.")); }}
                        className="h-9 px-3 rounded-md border border-neutral-200 text-xs font-semibold hover:bg-neutral-50">
                        {c.label}
                    </button>
                ))}
            </div>
        </div>
    );
}
